import { ApplicationConfig, APP_INITIALIZER, importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi, HttpClient } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MSAL_INSTANCE } from '@azure/msal-angular';
import { PublicClientApplication, IPublicClientApplication, BrowserCacheLocation } from '@azure/msal-browser';
import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { languageInterceptor } from './core/interceptors/language.interceptor';
import { HOTEL_SOURCING, FlightSearchHotelSourcing } from './core/services/hotel-sourcing.service';
import { environment } from '../environments/environment';

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

/** Single Azure AD B2C client for the whole app (redirect flow). */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.clientId,
      authority: environment.msalConfig.authority,
      knownAuthorities: environment.msalConfig.knownAuthorities,
      redirectUri: environment.msalConfig.redirectUri,
      postLogoutRedirectUri: environment.msalConfig.postLogoutRedirectUri,
      // We land back on redirectUri and let the Angular router route from there.
      navigateToLoginRequestUrl: false,
    },
    cache: { cacheLocation: BrowserCacheLocation.LocalStorage },
  });
}

/** Initialize MSAL and process the B2C redirect BEFORE the router/guards run, so the active
 *  account (and therefore AuthService.authed) is already correct on the first navigation. */
export function initializeMsal(instance: IPublicClientApplication): () => Promise<void> {
  return async () => {
    await instance.initialize();
    try {
      const result = await instance.handleRedirectPromise();
      if (result?.account) instance.setActiveAccount(result.account);
    } catch (e) {
      // A failed/cancelled redirect must not brick app bootstrap — stay unauthenticated.
      console.error('MSAL redirect handling failed', e);
    }
    if (!instance.getActiveAccount()) {
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) instance.setActiveAccount(accounts[0]);
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(withInterceptors([languageInterceptor, authInterceptor]), withInterceptorsFromDi()),
    provideAnimationsAsync(),
    // Azure AD B2C (MSAL) — login/redirect, and (Milestone 2) the real access token via authInterceptor.
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: APP_INITIALIZER, useFactory: initializeMsal, deps: [MSAL_INSTANCE], multi: true },
    // Sourcing seam — was ExtranetHotelSourcing (MVP); now FlightSearchHotelSourcing, backed by
    // be-flight-search-service's Amadeus connector. Extranet returns once Phase 3's backend
    // aggregation lands (both connectors merged behind the same /hotels/search endpoint) — see
    // ExtranetHotelSourcing below, kept as the base for that work, not dead code.
    { provide: HOTEL_SOURCING, useExisting: FlightSearchHotelSourcing },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: { provide: TranslateLoader, useFactory: HttpLoaderFactory, deps: [HttpClient] },
        useDefaultLang: true,
      })
    ),
  ],
};
